import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { ArrowRight, Calculator } from 'lucide-react';
import { CustomDropdown } from './CustomDropdown';

export const LandConverter = () => {
  const [fromValue, setFromValue] = useState('1');
  const [fromUnit, setFromUnit] = useState('Acre');
  const [toUnit, setToUnit] = useState('Katha');
  const [result, setResult] = useState('');
  const [selectedState, setSelectedState] = useState('West Bengal');

  // Accurate Indian land measurement conversions (to square feet)
  const getConversionRates = (state) => {
    const baseRates = {
      // Standard International Units
      'Square Feet': 1,
      'Square Meter': 10.764,
      'Square Yard': 9,
      'Acre': 43560,
      'Hectare': 107639,
      'Square Kilometer': 10763910.4,
      
      // Common Indian Units
      'Cent': 435.6,
      'Decimal': 435.6,
      'Guntha': 1089,
      'Ground': 2400,
      'Ankanam': 72,
      'Rood': 10890,
      'Chatak': 45,
      'Perch': 272.25,
      'Marla': 272.25,
      'Kanal': 5445,
      'Biswa': 1350,
    };

    // State-specific variations
    const stateSpecific = {
      'West Bengal': {
        'Bigha': 14400,  // 1 Bigha = 20 Katha
        'Katha': 720,    // 1 Katha = 720 sq ft
        'Cottah': 720,   // Same as Katha
      },
      'Bihar': {
        'Bigha': 27225,  // 1 Bigha = 20 Katha
        'Katha': 1361.25, // 1 Katha
        'Cottah': 68.0625,
      },
      'Assam': {
        'Bigha': 14400,
        'Katha': 2880,
        'Lecha': 144,
      },
      'Rajasthan': {
        'Bigha': 27225,
        'Biswa': 1361.25,
        'Pucca Bigha': 17424,
        'Kachha Bigha': 17424,
      },
      'Uttar Pradesh': {
        'Bigha': 27000,
        'Biswa': 1350,
        'Biswansi': 67.5,
      },
      'Punjab': {
        'Bigha': 9070,
        'Marla': 272.25,
        'Kanal': 5445,
        'Killa': 181400,
      },
      'Haryana': {
        'Bigha': 27225,
        'Biswa': 1361.25,
        'Biswansi': 68.0625,
      },
      'Gujarat': {
        'Bigha': 17424,
        'Vigha': 17424,
        'Guntha': 1089,
      },
      'Madhya Pradesh': {
        'Bigha': 12000,
        'Biswa': 600,
      },
      'Maharashtra': {
        'Guntha': 1089,
        'Acre': 43560,
      },
      'Karnataka': {
        'Guntha': 1089,
        'Cent': 435.6,
        'Acre': 43560,
      },
      'Tamil Nadu': {
        'Ground': 2400,
        'Cent': 435.6,
        'Ankanam': 72,
      },
      'Kerala': {
        'Cent': 435.6,
        'Acre': 43560,
      },
      'Andhra Pradesh': {
        'Guntha': 1089,
        'Cent': 435.6,
        'Acre': 43560,
      },
    };

    return { ...baseRates, ...(stateSpecific[state] || {}) };
  };

  const convertLand = () => {
    const value = parseFloat(fromValue) || 0;
    const rates = getConversionRates(selectedState);
    
    if (!rates[fromUnit] || !rates[toUnit]) {
      setResult('0');
      return;
    }
    
    // Convert to square feet first
    const sqFeet = value * rates[fromUnit];
    
    // Convert from square feet to target unit
    const resultValue = sqFeet / rates[toUnit];
    
    // Format result
    if (resultValue >= 1000) {
      setResult(resultValue.toLocaleString('en-IN', { maximumFractionDigits: 2 }));
    } else if (resultValue >= 1) {
      setResult(resultValue.toFixed(4));
    } else {
      setResult(resultValue.toFixed(6));
    }
  };

  useEffect(() => {
    if (fromValue) {
      convertLand();
    }
  }, [fromValue, fromUnit, toUnit, selectedState]);

  const rates = getConversionRates(selectedState);
  const units = Object.keys(rates).sort();

  const states = [
    'West Bengal', 'Bihar', 'Assam', 'Rajasthan', 'Uttar Pradesh',
    'Punjab', 'Haryana', 'Gujarat', 'Madhya Pradesh', 'Maharashtra',
    'Karnataka', 'Tamil Nadu', 'Kerala', 'Andhra Pradesh'
  ];

  return (
    <section id="calculator" className="am-reveal py-20 bg-gradient-to-br from-slate-50 to-emerald-50">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-4">
            <Calculator size={18} />
            Land Area Calculator
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Indian Land Area
            <span className="text-emerald-700"> Converter</span>
          </h2>
          <p className="text-lg text-slate-600">
            Convert between different land measurement units used across India with state-specific accuracy
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <Card className="border-2 border-emerald-200 shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
              <CardTitle className="text-2xl text-center flex items-center justify-center gap-3">
                <Calculator size={28} />
                Land Measurement Converter
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              {/* State Selector */}
              <div className="mb-8 p-4 bg-amber-50 border-2 border-amber-200 rounded-lg">
                <Label className="text-lg font-semibold text-slate-900 mb-3 block">
                  Select State (for accurate regional conversions)
                </Label>
                <CustomDropdown
                  value={selectedState}
                  options={states}
                  onChange={(value) => setSelectedState(value)}
                />
              </div>

              <div className="grid md:grid-cols-[1fr_auto_1fr] gap-8 items-end">
                {/* From Unit */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="fromValue" className="text-lg font-semibold mb-2 text-slate-900">
                      From Value
                    </Label>
                    <input
                      id="fromValue"
                      type="number"
                      step="any"
                      value={fromValue}
                      onChange={(e) => setFromValue(e.target.value)}
                      className="w-full px-4 py-4 text-3xl font-bold border-2 border-slate-300 rounded-lg focus:border-emerald-500 focus:outline-none"
                      placeholder="Enter value"
                    />
                  </div>
                  <div>
                    <CustomDropdown
                      label="Select Unit"
                      value={fromUnit}
                      options={units}
                      onChange={(value) => setFromUnit(value)}
                    />
                  </div>
                </div>

                {/* Arrow */}
                <div className="hidden md:flex justify-center items-center pb-8">
                  <div className="bg-emerald-100 p-4 rounded-full">
                    <ArrowRight size={32} className="text-emerald-700" />
                  </div>
                </div>

                {/* To Unit */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="result" className="text-lg font-semibold mb-2 text-slate-900">
                      Result
                    </Label>
                    <div className="w-full px-4 py-4 text-3xl font-bold bg-emerald-50 border-2 border-emerald-400 rounded-lg text-emerald-700 min-h-[76px] flex items-center">
                      {result || '0'}
                    </div>
                  </div>
                  <div>
                    <CustomDropdown
                      label="Select Unit"
                      value={toUnit}
                      options={units}
                      onChange={(value) => setToUnit(value)}
                    />
                  </div>
                </div>
              </div>

              {/* Conversion Info */}
              <div className="mt-8 p-6 bg-gradient-to-r from-emerald-50 to-amber-50 rounded-xl border-2 border-emerald-200">
                <p className="text-center text-slate-700 text-lg">
                  <span className="font-bold text-2xl text-slate-900">{fromValue} {fromUnit}</span>
                  {' = '}
                  <span className="font-bold text-2xl text-emerald-700">{result} {toUnit}</span>
                </p>
                <p className="text-center text-sm text-slate-600 mt-2">
                  Conversion based on {selectedState} standards
                </p>
              </div>

              {/* Quick Reference */}
              <div className="mt-8">
                <h3 className="text-lg font-bold text-slate-900 mb-4 text-center">Quick Reference ({selectedState})</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-white border-2 border-slate-200 rounded-lg text-center hover:border-emerald-300 transition-colors">
                    <div className="text-xs text-slate-600 mb-1">1 Acre</div>
                    <div className="text-sm font-bold text-emerald-700">43,560 sq.ft</div>
                  </div>
                  {rates['Bigha'] && (
                    <div className="p-4 bg-white border-2 border-slate-200 rounded-lg text-center hover:border-emerald-300 transition-colors">
                      <div className="text-xs text-slate-600 mb-1">1 Bigha</div>
                      <div className="text-sm font-bold text-emerald-700">{(rates['Bigha']).toLocaleString()} sq.ft</div>
                    </div>
                  )}
                  {rates['Katha'] && (
                    <div className="p-4 bg-white border-2 border-slate-200 rounded-lg text-center hover:border-emerald-300 transition-colors">
                      <div className="text-xs text-slate-600 mb-1">1 Katha</div>
                      <div className="text-sm font-bold text-emerald-700">{rates['Katha']} sq.ft</div>
                    </div>
                  )}
                  <div className="p-4 bg-white border-2 border-slate-200 rounded-lg text-center hover:border-emerald-300 transition-colors">
                    <div className="text-xs text-slate-600 mb-1">1 Hectare</div>
                    <div className="text-sm font-bold text-emerald-700">2.47 Acres</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
